import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo, LoadingState, useToast } from '@practium/ui';
import { useGetPublicPlans, useGetMe, useGetUpgradePlans, useSignPlan, useSignUpgradePlan, useTrackCheckoutStart } from '@practium/features/api';
import { authStorage } from '@practium/features/auth';
import { FlowStepIndicator } from '../components/FlowStepIndicator';
import { StepPersonalData, maskCpf, maskPhone } from '../steps/StepPersonalData';
import { StepPayment, type CheckoutPaymentMethod } from '../steps/StepPayment';
import { personalDataSchema } from '../validation';
import { DEFAULT_PERSONAL_DATA } from '../types';
import type { CheckoutPersonalData } from '../types';
import { getStoredSelectedPlanId, clearStoredSelectedPlanId, getStoredCheckoutMode } from '../selectedPlanStorage';
import { stateToUf, ufToState } from '../../properties/utils/masks';
import styles from '../checkoutShared.module.css';

const FLOW_STEP_LABELS = ['Cadastro', 'Plano', 'Pagamento', 'Confirmação'];

function stripBrazilCountryCode(value: string): string {
	const digits = value.replace(/\D/g, '');
	if (digits.length > 11 && digits.startsWith('55')) {
		return digits.slice(2);
	}
	return digits;
}

export const CheckoutPaymentPageClient = () => {
	const navigate = useNavigate();
	const toast = useToast();
	const { data: me } = useGetMe();
	const isUpgrade = getStoredCheckoutMode() === 'upgrade';
	const { data: publicPlans = [], isLoading: isLoadingPublic } = useGetPublicPlans();
	const { data: upgradePlans = [], isLoading: isLoadingUpgrade } = useGetUpgradePlans(isUpgrade);
	const plans = isUpgrade
		? [...upgradePlans, ...publicPlans].filter(
				(item, index, list) => list.findIndex((entry) => entry.plan._id === item.plan._id) === index
			)
		: publicPlans;
	const isLoadingPlans = isUpgrade ? isLoadingUpgrade || isLoadingPublic : isLoadingPublic;
	const { mutate: signPlan, isPending: isSigning } = useSignPlan();
	const { mutate: signUpgrade, isPending: isUpgrading } = useSignUpgradePlan();
	const isSubmitting = isSigning || isUpgrading;
	const { mutate: trackCheckoutStart } = useTrackCheckoutStart();
	const storedPlanId = getStoredSelectedPlanId();
	const selectedPlan = plans.find((item) => item.plan._id === storedPlanId);

	useEffect(() => {
		trackCheckoutStart({ step: 'pagamento', ...(storedPlanId ? { planId: storedPlanId } : {}) });
	}, []);

	const [personalData, setPersonalData] = useState<CheckoutPersonalData>(DEFAULT_PERSONAL_DATA);
	const [paymentMethod, setPaymentMethod] = useState<CheckoutPaymentMethod>('credit_card');

	useEffect(() => {
		if (!selectedPlan) return;
		const methods = selectedPlan.plan.paymentMethods;
		if (!methods.includes(paymentMethod)) {
			setPaymentMethod(methods.includes('credit_card') ? 'credit_card' : 'pix');
		}
	}, [selectedPlan, paymentMethod]);

	useEffect(() => {
		if (!me) return;
		setPersonalData((prev) => ({
			...prev,
			name: prev.name || me.name || '',
			surName: prev.surName || me.surName || '',
			document: prev.document || maskCpf(me.document || ''),
			phoneNumber: prev.phoneNumber || maskPhone(stripBrazilCountryCode(me.phoneNumber || '')),
			address: {
				...prev.address,
				zipCode: prev.address.zipCode || me.address?.zipCode || '',
				street: prev.address.street || me.address?.street || '',
				number: prev.address.number || me.address?.number || '',
				complement: prev.address.complement || me.address?.complement || '',
				district: prev.address.district || me.address?.district || '',
				city: prev.address.city || me.address?.city || '',
				state: prev.address.state || (me.address?.state ? ufToState(me.address.state) : ''),
			},
		}));
	}, [me]);

	useEffect(() => {
		if (!storedPlanId) {
			navigate(isUpgrade ? '/checkout/plano?upgrade=1' : '/checkout/plano', { replace: true });
			return;
		}
		if (!isLoadingPlans && plans.length && !selectedPlan) {
			clearStoredSelectedPlanId();
			navigate(isUpgrade ? '/checkout/plano?upgrade=1' : '/checkout/plano', { replace: true });
		}
	}, [storedPlanId, isLoadingPlans, plans.length, selectedPlan, navigate, isUpgrade]);

	const handleChangePlan = () => {
		clearStoredSelectedPlanId();
		navigate(isUpgrade ? '/checkout/plano?upgrade=1' : '/checkout/plano');
	};

	const canSubmit = () => personalDataSchema.safeParse(personalData).success;

	const handleSubmit = () => {
		if (!selectedPlan || !canSubmit()) return;

		const request = {
			planId: selectedPlan.plan._id,
			payload: {
				paymentMethod,
				name: personalData.name,
				surName: personalData.surName,
				document: personalData.document,
				documentType: 'cpf',
				phoneNumber: personalData.phoneNumber,
				address: {
					...personalData.address,
					state: stateToUf(personalData.address.state),
				},
			},
		};

		const options = {
			onSuccess: (data: {
				gatewayMetadata?: { checkoutUrl?: string | null };
				upgradeSubscription?: { checkoutUrl?: string | null };
			}) => {
				const checkoutUrl =
					data?.upgradeSubscription?.checkoutUrl || data?.gatewayMetadata?.checkoutUrl;
				if (checkoutUrl) {
					const token = authStorage.getToken();
					const user = authStorage.getUser();
					if (token && user) {
						authStorage.saveSession(user, token, true);
					}
					globalThis.location.href = checkoutUrl;
				} else {
					toast.error('Não foi possível iniciar o pagamento. Tente novamente.');
				}
			},
			onError: (err: unknown) => {
				toast.error(err instanceof Error ? err.message : 'Erro ao iniciar pagamento.');
			},
		};

		if (isUpgrade) {
			signUpgrade(request, options);
			return;
		}

		signPlan(request, options);
	};

	if (!selectedPlan || isSubmitting) {
		return <LoadingState fullScreen />;
	}

	return (
		<div className={styles.page}>
			<div className={styles.card}>
				<div className={styles.header}>
					<div className={styles.headerTop}>
						<Logo className={styles.logo} />
					</div>
					<h2 className={styles.title}>Dados e Pagamento</h2>
					<p className={styles.subtitle}>Preencha seus dados e continue para o pagamento.</p>
				</div>

				<div className={styles.stepsWrapper}>
					<FlowStepIndicator labels={FLOW_STEP_LABELS} currentStep={3} />
				</div>

				<div className={styles.content}>
					<div className={styles.stepSection}>
						<StepPersonalData data={personalData} onChange={setPersonalData} />
						<StepPayment
							plan={selectedPlan}
							onChangePlan={handleChangePlan}
							onSubmit={handleSubmit}
							isSubmitting={isSubmitting}
							canSubmit={canSubmit()}
							paymentMethod={paymentMethod}
							onChangePaymentMethod={setPaymentMethod}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
